import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldCheck, Users, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatNumberWithSeparator } from "@/lib/format";

const HARD_DEFAULTS = {
  currency: "IDR",
  ddf: 1.061,
  reach_level: 1,
  universe: 85114562,
  tv_cprp: 3537000,
  olv_ecpm: 22000,
  in_demo: 1.0,
  viewability: 1.0,
};

export default function Settings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [defaults, setDefaults] = useState(HARD_DEFAULTS);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(HARD_DEFAULTS);
  const [saving, setSaving] = useState(false);

  const canEdit = user?.role === "admin" || user?.role === "analyst";

  useEffect(() => {
    api.get("/settings/defaults").then(({ data }) => {
      setDefaults({ ...HARD_DEFAULTS, ...data });
    }).catch(() => {});
    if (user?.role === "admin") {
      setLoading(true);
      api.get("/users").then(({ data }) => { setUsers(data); setLoading(false); }).catch(() => setLoading(false));
    }
  }, [user]);

  const startEdit = () => { setDraft(defaults); setEditing(true); };
  const cancelEdit = () => { setDraft(defaults); setEditing(false); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        currency: draft.currency || "IDR",
        ddf: Number(draft.ddf),
        reach_level: Number(draft.reach_level),
        universe: Number(draft.universe),
        tv_cprp: Number(draft.tv_cprp),
        olv_ecpm: Number(draft.olv_ecpm),
        in_demo: Number(draft.in_demo),
        viewability: Number(draft.viewability),
      };
      const { data } = await api.put("/settings/defaults", payload);
      setDefaults({ ...HARD_DEFAULTS, ...data });
      setEditing(false);
      toast.success("Defaults saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl">
      <div>
        <h2 className="font-display text-2xl font-bold text-slate-900">Settings</h2>
        <p className="text-sm text-slate-500 mt-1">Brand, defaults, users and configuration.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="card-harmony p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3">App Branding</h3>
          <div className="flex items-center gap-4 p-4 rounded-lg bg-slate-50">
            <img src="/logo-harmony.png" className="h-16 w-16 bg-white rounded-lg p-2" alt="HARMONY" />
            <div>
              <div className="font-display text-xl font-bold text-[#0A3D91]">HARMONY</div>
              <div className="text-xs text-slate-500 uppercase tracking-widest">Media Optimization</div>
              <div className="text-xs text-slate-500 mt-1">Logo upload coming soon</div>
            </div>
          </div>
        </div>

        <div className="card-harmony p-5">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-display font-semibold text-slate-900">Defaults</h3>
              <p className="text-[11px] text-slate-500 mt-0.5">Used as fallback when creating new Vendor Parameters, Campaigns, Scenarios and Sensitivity runs.</p>
            </div>
            {!editing && canEdit && (
              <button onClick={startEdit} className="btn-secondary text-xs" data-testid="settings-edit-defaults">
                <Pencil className="h-3.5 w-3.5" /> Edit
              </button>
            )}
            {editing && (
              <div className="flex items-center gap-2">
                <button onClick={cancelEdit} className="btn-secondary text-xs" data-testid="settings-cancel-defaults"><X className="h-3.5 w-3.5" /> Cancel</button>
                <button onClick={save} disabled={saving} className="btn-primary text-xs" data-testid="settings-save-defaults">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
                </button>
              </div>
            )}
          </div>

          {!editing ? (
            <div className="space-y-2 text-sm">
              <Row label="Default Currency" value={defaults.currency} />
              <Row label="Default DDF (TV × OLV)" value={Number(defaults.ddf).toFixed(3)} />
              <Row label="Default Reach Level" value={`${defaults.reach_level}+`} />
              <Row label="Universe" value={formatNumberWithSeparator(defaults.universe)} />
              <Row label="Default TV CPRP" value={`${defaults.currency} ${formatNumberWithSeparator(defaults.tv_cprp)}`} />
              <Row label="Default OLV eCPM" value={`${defaults.currency} ${formatNumberWithSeparator(defaults.olv_ecpm)}`} />
              <Row label="Default In-Demo" value={Number(defaults.in_demo).toFixed(2)} />
              <Row label="Default Viewability" value={Number(defaults.viewability).toFixed(2)} />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <EditField label="Currency" value={draft.currency} onChange={(v) => setDraft({ ...draft, currency: v })} testId="defaults-currency" />
              <EditField label="DDF" type="number" step="0.001" value={draft.ddf} onChange={(v) => setDraft({ ...draft, ddf: v })} testId="defaults-ddf" />
              <EditField label="Reach Level (e.g. 1, 3)" type="number" value={draft.reach_level} onChange={(v) => setDraft({ ...draft, reach_level: v })} testId="defaults-reach-level" />
              <EditField label="Universe" format="integer" value={draft.universe} onChange={(v) => setDraft({ ...draft, universe: v })} testId="defaults-universe" />
              <EditField label="TV CPRP" format="integer" value={draft.tv_cprp} onChange={(v) => setDraft({ ...draft, tv_cprp: v })} testId="defaults-tv-cprp" />
              <EditField label="OLV eCPM" format="integer" value={draft.olv_ecpm} onChange={(v) => setDraft({ ...draft, olv_ecpm: v })} testId="defaults-olv-ecpm" />
              <EditField label="In-Demo (0-1)" type="number" step="0.01" value={draft.in_demo} onChange={(v) => setDraft({ ...draft, in_demo: v })} testId="defaults-in-demo" />
              <EditField label="Viewability (0-1)" type="number" step="0.01" value={draft.viewability} onChange={(v) => setDraft({ ...draft, viewability: v })} testId="defaults-viewability" />
            </div>
          )}
        </div>

        <div className="card-harmony p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3">Formula Notes</h3>
          <div className="text-xs text-slate-600 space-y-2 leading-relaxed">
            <div><b>TV GRP</b> = TV Budget ÷ TV CPRP</div>
            <div><b>OLV Impressions</b> = (OLV Budget ÷ OLV eCPM) × 1000</div>
            <div><b>Effective Impressions</b> = Impressions × In-Demo × Viewability</div>
            <div><b>Combined Reach</b> = TV Reach + OLV Reach − (DDF × TV × OLV) <br /><span className="text-slate-400">(all reaches as decimal)</span></div>
            <div><b>Effective CPRP</b> = Total Budget ÷ (Combined Reach × 100)</div>
            <div><b>Saving vs TV Only</b> = (TV-only cost to match reach) − Actual Total Budget</div>
          </div>
        </div>

        <div className="card-harmony p-5">
          <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-[#0A3D91]" /> Your Account
          </h3>
          <div className="space-y-2 text-sm">
            <Row label="Name" value={user?.name} />
            <Row label="Email" value={user?.email} />
            <Row label="Role" value={user?.role?.toUpperCase()} />
          </div>
        </div>

        <div className="card-harmony p-5 lg:col-span-2">
          <h3 className="font-display font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Users className="h-4 w-4 text-[#0A3D91]" /> User Management
          </h3>
          {user?.role !== "admin" ? (
            <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">Admin access required to manage users.</div>
          ) : loading ? (
            <div className="py-8 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <tr>
                  <th className="text-left py-2 px-3">Name</th>
                  <th className="text-left py-2 px-3">Email</th>
                  <th className="text-left py-2 px-3">Role</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="py-2 px-3 font-medium">{u.name}</td>
                    <td className="py-2 px-3 text-slate-600">{u.email}</td>
                    <td className="py-2 px-3">
                      <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#0A3D91]/10 text-[#0A3D91] font-semibold">
                        {u.role}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
      <span className="text-slate-500">{label}</span>
      <span className="font-medium text-slate-900">{value}</span>
    </div>
  );
}

function EditField({ label, value, onChange, type = "text", step, format, testId }) {
  const isInteger = format === "integer";
  const handleChange = (e) => {
    const v = e.target.value;
    onChange(isInteger ? v.replace(/[^\d]/g, "") : v);
  };
  const displayValue = isInteger ? formatNumberWithSeparator(value) : value;
  return (
    <div>
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">{label}</label>
      <input
        type={isInteger ? "text" : type}
        inputMode={isInteger ? "numeric" : undefined}
        autoComplete="off"
        step={!isInteger ? step : undefined}
        value={displayValue ?? ""}
        onChange={handleChange}
        data-testid={testId}
        className="mt-1 w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#0E73C9] focus:border-[#0E73C9] outline-none"
      />
    </div>
  );
}
